import React from 'react';
import { ListView, GridView } from '@one-thd/sui-icons';
import {
  Button,
  SkeletonBlock,
  SkeletonContent
} from '@one-thd/sui-atomic-components';
import proptypes from 'prop-types';
const { func, string, bool, arrayOf, shape } = proptypes;
import {
  CARD_VIEW,
  TABLE_VIEW,
  CARD_VIEW_TYPE,
  TABLE_VIEW_TYPE,
  ANALYTICS_CLEAR_FILTERS
} from '../project-summary-helper';
import { hasActiveFilters, clearFiltersAnalytics } from '../../helpers';
import { FilterDrawer } from '../FilterDrawer/FilterDrawer';

export const ToggleLayoutBar = ({
  handleViewChange,
  currentView,
  projects,
  loading,
  onApplyFilters,
  filterCriteria,
  showLayoutToggles = true
}) => {

  const SelectedListViewIcon = () => <ListView color="brand" size="small" />;
  const SelectedGridViewIcon = () => <GridView color="brand" size="small" />;
  const unselectedListViewIcon = () => <ListView color="primary" size="small" />;
  const UnselectedGridViewIcon = () => <GridView color="primary" size="small" />;

  const handleClearFilters = () => {
    // Determine view type for analytics
    const viewType = currentView === CARD_VIEW ? CARD_VIEW_TYPE : TABLE_VIEW_TYPE;

    // Trigger clear filters analytics
    clearFiltersAnalytics({
      viewType,
      clearType: ANALYTICS_CLEAR_FILTERS
    });

    onApplyFilters({
      projectName: '',
      projectNumber: '',
      projectStatus: ''
    });
  };

  if (!projects?.length && loading) {
    return (
      <div
        data-testid="projects-toggle-placeholder"
        className="sui-grid sui-grid-cols-4 sui-gap-2 sui-py-6"
      >
        <div className="sui-col-start-4">
          <SkeletonContent disablePadding initialSize="1/4" orientation="horizontal" grow>
            <SkeletonBlock height={11} aspect="wide" />
            <SkeletonBlock height={11} aspect="wide" />
          </SkeletonContent>
        </div>
      </div>
    );
  }

  return (
    <div className="sui-flex sui-justify-end">
      <div className="sui-flex sui-gap-2 sui-py-6">
        {hasActiveFilters(filterCriteria) && (
          <Button
            variant="tertiary"
            onClick={handleClearFilters}
            data-testid="clear-filters-button"
          >
            Clear Filters
          </Button>
        )}
        <FilterDrawer
          onApplyFilters={onApplyFilters}
          initialFilters={filterCriteria}
          viewType={currentView === CARD_VIEW ? CARD_VIEW_TYPE : TABLE_VIEW_TYPE}
        />
        {showLayoutToggles && (
          <>
            <Button
              startIcon={currentView === TABLE_VIEW ? SelectedListViewIcon : unselectedListViewIcon}
              variant="ghost"
              onClick={() => handleViewChange(TABLE_VIEW)}
              data-testid="projects-view-toggle--table"
              disabled={currentView === TABLE_VIEW}
            >
              Table View
            </Button>
            <Button
              startIcon={currentView === CARD_VIEW ? SelectedGridViewIcon : UnselectedGridViewIcon}
              variant="ghost"
              onClick={() => handleViewChange(CARD_VIEW)}
              data-testid="projects-view-toggle--card"
              disabled={currentView === CARD_VIEW}
            >
              Card View
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

ToggleLayoutBar.displayName = 'ToggleLayoutBar';

ToggleLayoutBar.propTypes = {
  handleViewChange: func,
  currentView: string,
  projects: arrayOf(shape({})),
  loading: bool,
  onApplyFilters: func,
  filterCriteria: shape({
    projectName: string,
    projectNumber: string,
    projectStatus: string
  }),
  showLayoutToggles: bool
};

ToggleLayoutBar.defaultProps = {
  currentView: CARD_VIEW,
  handleViewChange: () => {},
  projects: [],
  loading: false,
  onApplyFilters: () => {},
  filterCriteria: {
    projectName: '',
    projectNumber: '',
    projectStatus: ''
  },
  showLayoutToggles: true
};